// src/api/api.js

export async function apiFetch(url, options = {}) {
  const handleAuthError = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');

    // Try React Router's navigate if globally available
    if (window.routerNavigate) {
      window.routerNavigate('/login');
    } else {
      window.location.href = '/login';
    }
  };

  const token = localStorage.getItem('token');

  const isTokenExpired = (token) => {
    try {
      const { exp } = JSON.parse(atob(token.split('.')[1]));
      return Date.now() >= exp * 1000; // exp in seconds
    } catch (err) {
      return true;
    }
  };

  // 🔐 Token validation BEFORE sending request
  if (!token || isTokenExpired(token)) {
    handleAuthError();
    return Promise.reject(new Error('Session expired'));
  }

  try {

    // Don't set Content-Type for FormData - let browser handle it
    const isFormData = options.body instanceof FormData;
    const defaultHeaders = isFormData ? {} : { 'Content-Type': 'application/json' };
    
    const res = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(options.headers || {})
      }
    });

    const contentType = res.headers.get('content-type') || '';

    // 🛑 Handle raw errors first
    if (!contentType.includes('application/json')) {
      if (res.status === 401) {
        handleAuthError();
        throw new Error('Session expired. Please log in again.');
      }
      if (res.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      }
      throw new Error(`API returned non-JSON response: ${res.statusText}`);
    }

    const result = await res.json();

    // 🎯 Handle HTTP errors
    if (!res.ok) {
      if (res.status === 401) {
        handleAuthError();
        throw new Error('Session expired. Please log in again.');
      }
      if (res.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      }
      
      // For structured error responses (like foreign key constraint errors)
      if (result && result.error && result.message) {
        throw new Error(result.message);
      }
      
      throw new Error(result.message || `API request failed: ${res.statusText}`);
    }

    return result.data || result;
  } catch (error) {
    if (
      error.message.includes('Session expired') ||
      error.message.includes('Access denied') ||
      error.message.includes('API request failed')
    ) {
      throw error;
    }

    throw new Error(`Network error: ${error.message}`);
  }
}

// 📊 Fetch latest DDR data for a specific table and well
export async function getLatestDDRData(wellId, tableName, beforeDate = null) {
  try {
    let url = `/api/ddr/latest-data/${wellId}/${tableName}`;
    if (beforeDate) {
      url += `?beforeDate=${beforeDate}`;
    }
    
    // Get raw response without automatic data extraction
    const token = localStorage.getItem('token');
    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }

    const result = await res.json();
    
    console.log('🔍 Raw backend response for getLatestDDRData:', {
      url,
      status: res.status,
      rawResult: result,
      hasData: !!(result && result.data),
      dataLength: Array.isArray(result?.data) ? result.data.length : 'not array',
      resultKeys: result ? Object.keys(result) : []
    });
    
    return result; // Return the full response object with data, latestReportDate, etc.
  } catch (error) {
    console.error(`❌ Error fetching latest DDR data for ${tableName}:`, error);
    throw error;
  }
}
